if (!customElements.get('bf-product-gallery')) {
  customElements.define(
    'bf-product-gallery',
    class BFProductGallery extends HTMLElement {
      connectedCallback() {
        this.abortController?.abort();
        this.abortController = new AbortController();
        const { signal } = this.abortController;
        this.viewport = this.querySelector('[data-bf-gallery-viewport]');
        this.list = this.querySelector('[data-bf-gallery-list]');
        this.slides = Array.from(this.querySelectorAll('[data-bf-gallery-slide]'));
        this.thumbnails = Array.from(this.querySelectorAll('[data-bf-gallery-thumbnail]'));
        this.previousButton = this.querySelector('[data-bf-gallery-previous]');
        this.nextButton = this.querySelector('[data-bf-gallery-next]');
        this.status = this.querySelector('[data-bf-gallery-status]');
        this.mediaQuery = window.matchMedia('(max-width: 989px)');
        const initialIndex = this.slides.findIndex((slide) => slide.dataset.active === 'true');
        this.currentIndex = initialIndex >= 0 ? initialIndex : 0;
        this.scrollFrame = null;

        this.onPrevious = () => this.select(this.currentIndex - 1, true);
        this.onNext = () => this.select(this.currentIndex + 1, true);
        this.onScroll = () => this.handleScroll();
        this.onKeydown = (event) => this.handleKeydown(event);
        this.onModeChange = () => this.syncMode();

        this.previousButton?.addEventListener('click', this.onPrevious, { signal });
        this.nextButton?.addEventListener('click', this.onNext, { signal });
        this.list?.addEventListener('scroll', this.onScroll, { passive: true, signal });
        this.viewport?.addEventListener('keydown', this.onKeydown, { signal });
        this.mediaQuery.addEventListener?.('change', this.onModeChange, { signal });

        this.thumbnails.forEach((thumbnail) => {
          thumbnail.addEventListener('click', () => {
            this.select(Number(thumbnail.dataset.mediaIndex), true);
          }, { signal });
        });

        this.syncMode();
      }

      disconnectedCallback() {
        this.abortController?.abort();
        this.abortController = null;
        if (this.scrollFrame) window.cancelAnimationFrame(this.scrollFrame);
      }

      handleKeydown(event) {
        if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
        event.preventDefault();
        this.select(this.currentIndex + (event.key === 'ArrowRight' ? 1 : -1), true);
      }

      handleScroll() {
        if (!this.mediaQuery.matches || this.scrollFrame) return;
        this.scrollFrame = window.requestAnimationFrame(() => {
          this.scrollFrame = null;
          const width = this.list?.clientWidth || 1;
          this.select(Math.round((this.list?.scrollLeft || 0) / width), false);
        });
      }

      select(index, shouldScroll) {
        if (!this.slides.length) return;
        const nextIndex = Math.max(0, Math.min(index, this.slides.length - 1));
        this.currentIndex = nextIndex;

        this.slides.forEach((slide, slideIndex) => {
          const isActive = slideIndex === nextIndex;
          slide.dataset.active = String(isActive);
          slide.setAttribute('aria-current', String(isActive));
          slide.setAttribute('aria-hidden', String(!this.mediaQuery.matches && !isActive));
          const zoom = slide.querySelector('[data-bf-media-zoom]');
          if (zoom) zoom.tabIndex = isActive ? 0 : -1;
        });

        this.thumbnails.forEach((thumbnail, thumbnailIndex) => {
          thumbnail.setAttribute('aria-current', String(thumbnailIndex === nextIndex));
        });

        if (this.previousButton) this.previousButton.disabled = nextIndex === 0 || this.slides.length <= 1;
        if (this.nextButton) this.nextButton.disabled = nextIndex === this.slides.length - 1 || this.slides.length <= 1;
        if (this.status) this.status.textContent = `Media ${nextIndex + 1} of ${this.slides.length}`;

        if (shouldScroll && this.mediaQuery.matches && this.list) {
          this.list.scrollTo({
            left: this.list.clientWidth * nextIndex,
            behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth',
          });
        }
      }

      selectMediaById(mediaId, shouldScroll = true) {
        const nextIndex = this.slides.findIndex((slide) => slide.dataset.mediaId === String(mediaId));
        if (nextIndex >= 0) this.select(nextIndex, shouldScroll);
      }

      syncMode() {
        if (this.mediaQuery.matches && this.list) {
          this.list.scrollLeft = this.list.clientWidth * this.currentIndex;
        }
        this.select(this.currentIndex, false);
      }
    }
  );
}

// Reuse the storefront modal's focus trap, Escape/close and focus restoration.
if (!customElements.get('bf-product-media-modal')) {
  customElements.define('bf-product-media-modal', class extends ModalDialog {
    show(opener) {
      const source = opener.closest('[data-bf-gallery-slide]')?.querySelector('img');
      if (!source || !opener.dataset.zoomSrc) return;
      const image = new Image();
      image.src = opener.dataset.zoomSrc;
      image.alt = source.alt;
      image.width = Number(source.getAttribute('width')) || source.naturalWidth;
      image.height = Number(source.getAttribute('height')) || source.naturalHeight;
      const content = this.querySelector('.bf-product-media-modal__content');
      content.replaceChildren(image);
      super.show(opener);
      content.scrollTop = 0;
      image.addEventListener('load', () => {
        content.scrollLeft = Math.max(0, (image.width - content.clientWidth) / 2);
      }, { once: true });
    }
  });
}
